import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon,
  XMarkIcon, PencilIcon, PlusIcon, UserPlusIcon
} from '@heroicons/react/24/outline';
import { SERVER_URL } from "../config";
import axios from 'axios';
import TableHeaderFilter from './TableHeaderfilter';
import SearchableDropdown from './SearchableDropdown';

/* ── shared input style ─────────────────────────────────────────────────── */
const inputStyle =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm " +
  "text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 " +
  "focus:border-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-100";

/* ── lead status / reference options (used on the Mark-as-Lead form) ────── */
const STATUS_OPTIONS = [
  'New Lead',
  'Estimate Required',
  'Estimate Submitted',
  'Follow up Running',
  'Negotiation',
  'Order Confirmed',
  'Completed & Review Collected',
  'Not Interested',
  'Lost',
];

const REFERENCE_OPTIONS = [
  'By Call',
  'Showroom',
  'Social Media',
  'Website',
  'Walk-in',
  'Email',
  'Reference',
  'Exhibition',
  'Other',
];

/* ── format a datetime string as Indian time ─────────────────────────────── */
const formatIST = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

/* ════════════════════════════════════════════════════════════════════════ */
const LedgerTable = ({
  ledgers = [],
  currentPage = 1,
  limit = 10,
  onPageChange = () => { },
  onLimitChange = () => { },
  searchQuery = '',
  onFilteredDataChange = () => { },
  onRefresh = () => { },
  onClose = null,
}) => {
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [addLedgerModal, setAddLedgerModal] = useState(false);
  const [editLedgerModal, setEditLedgerModal] = useState(false);
  const [markAsLeadModal, setMarkAsLeadModal] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [errors, setErrors] = useState({});
  const [leadErrors, setLeadErrors] = useState({});
  const [saving, setSaving] = useState(false);

  /* options fetched from backend */
  const [productNameOptions, setProductNameOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);

  /* table column filters */
  const [ledgerNameFilter, setLedgerNameFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState([]);
  const [branchFilter, setBranchFilter] = useState([]);
  const [productFilter, setProductFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  const employee = JSON.parse(localStorage.getItem("employee")) || {
    employee_name: "",
    employee_id: ""
  };

  /* ── blank ledger (Add / Edit form) ─────────────────────────────────── */
  const blankLedger = {
    slNo: '',
    masteId: '',
    alterId: '',
    ledgerId: '',
    ledger_name: '',
    phone_number: '',
    address: '',
    gst_no: '',
    state: '',
    country: '',
    pincode: '',
    ledger_path: '',
    is_active: 1,
    remarks: '',
    product: '',
    companyName: '',
    branch: '',
  };
  const [newLedger, setNewLedger] = useState({ ...blankLedger });

  /* ── blank lead (Mark-as-Lead form) ─────────────────────────────────── */
  const blankLead = {
    slNo: '',
    companyName: '',
    category: '',
    contactPerson: '',
    mobileNumber: '',
    alternateMobileNumber: '',
    branch: '',
    mailId: '',
    address: '',
    location: '',
    pinCode: '',
    quotationNo: '',
    reference: '',
    referenceName: '',
    qty: '',
    commission_amount: '',
    closedAmount: '',
    leadCreatedDate: '',
    nextFollowUp: '',
    status: 'New Lead',
    remarks: '',
  };
  const [newLead, setNewLead] = useState({ ...blankLead });

  /* ════════════════════════════════════════════════════════════════════
     LEDGER FORM FIELDS
  ════════════════════════════════════════════════════════════════════ */
  const ledgerFormFields = [
    {
      name: 'ledger_name', label: 'Ledger Name', type: 'text',
      placeholder: 'Ledger Name', required: true,
    },
    {
      name: 'companyName', label: 'Company Name', type: 'text',
      placeholder: 'Company Name',
    },
    {
      name: 'branch', label: 'Branch', type: 'select',
      placeholder: 'Select Branch', options: 'branches',
    },
    {
      name: 'product', label: 'Product', type: 'select',
      placeholder: 'Select Product', options: 'productNames',
    },
    {
      name: 'phone_number', label: 'Phone Number', type: 'tel',
      placeholder: 'Phone Number',
    },
    {
      name: 'gst_no', label: 'GST No.', type: 'text',
      placeholder: 'GST Number',
    },
    {
      name: 'address', label: 'Address', type: 'text',
      placeholder: 'Address',
    },
    {
      name: 'state', label: 'State', type: 'text',
      placeholder: 'State',
    },
    {
      name: 'country', label: 'Country', type: 'text',
      placeholder: 'Country',
    },
    {
      name: 'pincode', label: 'Pincode', type: 'text',
      placeholder: 'Pincode',
    },
    {
      name: 'ledger_path', label: 'Ledger Path', type: 'text',
      placeholder: 'e.g. primary>current assets>sundry debtors',
    },
    {
      name: 'is_active', label: 'Status', type: 'select',
      placeholder: 'Select Status', options: 'activeStatus',
    },
    {
      name: 'masteId', label: 'Master ID (Tally)', type: 'text',
      placeholder: 'Master ID', nonEditable: true,
    },
    {
      name: 'alterId', label: 'Alter ID (Tally)', type: 'text',
      placeholder: 'Alter ID', nonEditable: true,
    },
    {
      name: 'remarks', label: 'Remarks', type: 'textarea',
      placeholder: 'Remarks', colSpan: 2,
    },
  ];

  const nonEditableLedgerFields = ['masteId', 'alterId'];

  /* ════════════════════════════════════════════════════════════════════
     LEAD FORM FIELDS (Mark-as-Lead)
  ════════════════════════════════════════════════════════════════════ */
  const leadFormFields = [
    {
      name: 'companyName', label: 'Company Name', type: 'text',
      placeholder: 'Company Name', required: true, nonEditable: true,
    },
    {
      name: 'category', label: 'Category / Product', type: 'select',
      placeholder: 'Select Category', options: 'productNames', required: true,
    },
    {
      name: 'contactPerson', label: 'Contact Person', type: 'text',
      placeholder: 'Contact Person Name & Designation',
    },
    {
      name: 'mobileNumber', label: 'Contact No.', type: 'tel',
      placeholder: 'Phone No.', required: true,
    },
    {
      name: 'alternateMobileNumber', label: 'Alternate Contact No.', type: 'tel',
      placeholder: 'Alternate Number',
    },
    {
      name: 'mailId', label: 'Email ID', type: 'email',
      placeholder: 'E-mail Id',
    },
    {
      name: 'address', label: 'Address', type: 'text',
      placeholder: 'Address', required: true,
    },
    {
      name: 'location', label: 'Location', type: 'text',
      placeholder: 'Location / Area',
    },
    {
      name: 'pinCode', label: 'Pincode', type: 'text',
      placeholder: 'Pincode',
    },
    {
      name: 'branch', label: 'Branch', type: 'select',
      placeholder: 'Select Branch', options: 'branches', required: true,
    },
    {
      name: 'reference', label: 'Reference', type: 'select',
      placeholder: 'Select Reference Source', options: 'referenceOpts',
    },
    {
      name: 'referenceName', label: 'Reference Name', type: 'text',
      placeholder: 'Reference Person Name',
    },
    {
      name: 'qty', label: 'Quantity', type: 'number',
      placeholder: 'Enter Quantity',
    },
    {
      name: 'commission_amount', label: 'Commission Amount', type: 'number',
      placeholder: 'Enter Commission Amount',
    },
    {
      name: 'closedAmount', label: 'Closed Amount', type: 'number',
      placeholder: 'Enter Closed Amount',
    },
    {
      name: 'nextFollowUp', label: 'Next Follow Up', type: 'datetime-local',
      placeholder: '',
    },
    {
      name: 'status', label: 'Status', type: 'select',
      placeholder: 'Select Status', options: 'statusOptions', required: true,
    },
    {
      name: 'remarks', label: 'Remarks', type: 'textarea',
      placeholder: 'Remarks / Follow-up notes', colSpan: 2,
    },
  ];

  /* ════════ FETCH ════════ */
  const fetchProductNames = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/Rahul/fetchProductAndCategory.php`);
      if (res.data.success) {
        const names = (res.data.products || [])
          .filter(item => item.status === "ACTIVE")
          .map(item => item.name)
          .filter(Boolean);
        setProductNameOptions([...new Set(names)]);
      }
    } catch (error) {
      console.error('productDetailsFetch error', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/branchFetch.php`, {
        employee_id: employee.employee_id,
      });
      if (res.data.status) {
        const branches = res.data.branches || [];
        const userRegion = res.data.user_region || "";
        setBranchOptions(branches);
        const matched = branches.find(b => b.toLowerCase() === userRegion.toLowerCase());
        if (matched) setNewLead(prev => ({ ...prev, branch: matched }));
      }
    } catch { console.error('branchFetch error'); }
  };

  useEffect(() => {
    fetchProductNames();
    fetchBranches();
  }, []);

  useEffect(() => { setSearchTerm(searchQuery); }, [searchQuery]);

  /* ════════ VALIDATION ════════ */
  const validateLedger = (ledger) => {
    const errs = {};
    if (!ledger.ledger_name) errs.ledger_name = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateLead = (lead) => {
    const errs = {};
    if (!lead.companyName) errs.companyName = true;
    if (!lead.mobileNumber) errs.mobileNumber = true;
    if (!lead.branch) errs.branch = true;
    if (!lead.address) errs.address = true;
    if (!lead.category) errs.category = true;
    setLeadErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ════════ SAVE NEW LEDGER ════════ */
  const saveLedger = async () => {
    if (!validateLedger(newLedger)) { alert("Required fields are missing."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${SERVER_URL}/DataInsert/insertNewLedger.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...newLedger,
          employee_id: employee.employee_id,
        }),
      });
      const result = await res.json();
      if (result.status === "success") {
        setNewLedger({ ...blankLedger });
        alert("Ledger Created.");
        setAddLedgerModal(false);
        onRefresh();
      } else { alert(result.message || "Failed to create ledger."); }
    } catch { alert("Network error. Please try again."); }
    setSaving(false);
  };

  /* ════════ UPDATE LEDGER ════════ */
  const updateLedger = async () => {
    if (!validateLedger(selectedLedger)) { alert("Required fields are missing."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${SERVER_URL}/DataUpdate/ledgerUpdate.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...selectedLedger,
          employee_id: employee.employee_id,
        }),
      });
      if (!res.ok) throw new Error("Network error");
      const result = await res.json();
      if (result.status === "success") {
        alert("Ledger Updated Successfully.");
        setEditLedgerModal(false);
        onRefresh();
      } else { alert(result.message || "Failed to update ledger."); }
    } catch { alert("Error updating ledger."); }
    setSaving(false);
  };

  /* ════════ CREATE LEAD FROM LEDGER ════════ */
  const saveLeadFromLedger = async () => {
    if (!validateLead(newLead)) { alert("Required fields are missing."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${SERVER_URL}/DataInsert/insertNewLead.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...newLead, employee }),
      });
      const result = await res.json();
      if (result.status === "success") {
        setNewLead({ ...blankLead });
        alert("Lead Created.");
        setMarkAsLeadModal(false);
        onRefresh();
      } else { alert(result.message || "Failed to create lead."); }
    } catch { alert("Network error. Please try again."); }
    setSaving(false);
  };

  /* ════════ ROW ACTIONS ════════ */
  const openEditLedger = (ledger, e) => {
    e.stopPropagation();
    setSelectedLedger({ ...ledger });
    setErrors({});
    setEditLedgerModal(true);
  };

  const openMarkAsLead = (ledger, e) => {
    e.stopPropagation();
    setNewLead({
      ...blankLead,
      companyName: ledger.companyName || ledger.ledger_name || '',
      category: ledger.product || '',
      address: ledger.address || '',
      pinCode: ledger.pincode || '',
    });
    setLeadErrors({});
    setMarkAsLeadModal(true);
  };

  const handleNewLedgerChange = (e) => {
    const { name, value } = e.target;
    setNewLedger(prev => ({ ...prev, [name]: value }));
  };

  const handleEditLedgerChange = (e) => {
    const { name, value } = e.target;
    setSelectedLedger(prev => ({ ...prev, [name]: value }));
  };

  const handleNewLeadChange = (e) => {
    const { name, value } = e.target;
    setNewLead(prev => ({ ...prev, [name]: value }));
  };

  /* ════════ TABLE FILTER OPTIONS ════════ */
  const uniqOpts = (arr) => [...new Set(arr)].map(v => ({ label: String(v), value: v }));

  const ledgerNameOptions = useMemo(() => uniqOpts(ledgers.map(l => l.ledger_name).filter(Boolean)), [ledgers]);
  const companyOptions = useMemo(() => uniqOpts(ledgers.map(l => l.companyName).filter(Boolean)), [ledgers]);
  const branchOptionsFilter = useMemo(() => uniqOpts(ledgers.map(l => l.branch).filter(Boolean)), [ledgers]);
  const productOptionsFilter = useMemo(() => uniqOpts(ledgers.map(l => l.product).filter(Boolean)), [ledgers]);
  const stateOptionsFilter = useMemo(() => uniqOpts(ledgers.map(l => l.state).filter(Boolean)), [ledgers]);
  const statusOptionsFilter = useMemo(() => ([
    { label: 'Active', value: 1 },
    { label: 'Inactive', value: 0 },
  ]), []);

  /* ════════ FILTERED LEDGERS ════════ */
  const filteredLedgers = useMemo(() => ledgers.filter(ledger => {
    const q = searchTerm.toLowerCase();
    const matchSearch =
      (ledger.ledger_name?.toLowerCase() || "").includes(q) ||
      (ledger.companyName?.toLowerCase() || "").includes(q) ||
      (ledger.phone_number?.toString() || "").includes(q) ||
      (ledger.gst_no?.toLowerCase() || "").includes(q);
    const matchLedgerName = ledgerNameFilter.length === 0 || ledgerNameFilter.includes(ledger.ledger_name);
    const matchCompany = companyFilter.length === 0 || companyFilter.includes(ledger.companyName);
    const matchBranch = branchFilter.length === 0 || branchFilter.includes(ledger.branch);
    const matchProduct = productFilter.length === 0 || productFilter.includes(ledger.product);
    const matchState = stateFilter.length === 0 || stateFilter.includes(ledger.state);
    const matchStatus = statusFilter.length === 0 || statusFilter.includes(Number(ledger.is_active));
    return matchSearch && matchLedgerName && matchCompany && matchBranch && matchProduct && matchState && matchStatus;
  }), [ledgers, searchTerm, ledgerNameFilter, companyFilter, branchFilter, productFilter, stateFilter, statusFilter]);

  useEffect(() => { onFilteredDataChange(filteredLedgers); }, [filteredLedgers]);

  /* ════════ DROPDOWN OPTIONS PER FIELD ════════ */
  const toOpts = (arr = []) => arr.map(v => ({ label: String(v), value: v }));

  const getFieldOptions = (field) => {
    switch (field.options) {
      case 'productNames': return toOpts(productNameOptions);
      case 'branches': return branchOptions.map(b => ({ label: b, value: b.toLowerCase() }));
      case 'referenceOpts': return toOpts(REFERENCE_OPTIONS);
      case 'statusOptions': return toOpts(STATUS_OPTIONS);
      case 'activeStatus': return [{ label: 'Active', value: 1 }, { label: 'Inactive', value: 0 }];
      default:
        if (Array.isArray(field.options)) return toOpts(field.options);
        return [];
    }
  };

  /* ════════ RENDER ONE FIELD ════════ */
  const renderFormField = (field, value, onChange, disabled = false) => {
    const fieldValue = value ?? "";
    const hasError = (field.errsSource || errors)[field.name];
    const isDisabled = disabled || field.readOnly;

    if (field.type === "textarea") {
      return (
        <textarea
          className={
            "w-full bg-slate-50 border rounded-lg px-3 py-2 text-sm text-slate-700 " +
            "placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 " +
            "transition-colors resize-none disabled:opacity-50 disabled:bg-slate-100 " +
            (hasError ? "border-rose-400" : "border-slate-200")
          }
          rows={3}
          placeholder={field.placeholder}
          name={field.name}
          value={fieldValue}
          onChange={onChange}
          disabled={isDisabled}
        />
      );
    }

    if (field.type === "select" || field.options) {
      return (
        <>
          <SearchableDropdown
            label=""
            options={getFieldOptions(field)}
            value={fieldValue}
            placeholder={field.placeholder}
            disabled={isDisabled}
            onChange={(val) => onChange({ target: { name: field.name, value: val } })}
          />
          {hasError && <p className="text-rose-500 text-xs mt-1">Required field</p>}
        </>
      );
    }

    return (
      <>
        <input
          className={`${inputStyle} ${hasError ? "border-rose-400 focus:ring-rose-400" : ""}`}
          type={field.type}
          placeholder={field.placeholder}
          name={field.name}
          value={fieldValue}
          onChange={onChange}
          disabled={isDisabled}
          readOnly={field.readOnly}
        />
        {hasError && <p className="text-rose-500 text-xs mt-1">Required field</p>}
      </>
    );
  };

  const startEntry = filteredLedgers.length > 0 ? (currentPage - 1) * limit + 1 : 0;
  const endEntry = Math.min(currentPage * limit, filteredLedgers.length);
  const totalPagesCalc = Math.ceil(filteredLedgers.length / limit);

  /* ══════════════════════════════════ JSX ══════════════════════════════════ */
  return (
    <>
      <style>{`
        .att-scrollbar::-webkit-scrollbar        { width:5px; height:5px; }
        .att-scrollbar::-webkit-scrollbar-track  { background:#f1f5f9; border-radius:10px; }
        .att-scrollbar::-webkit-scrollbar-thumb  { background:#cbd5e1; border-radius:10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background:#94a3b8; }
      `}</style>

      <div
        className={onClose ? "fixed inset-0 z-40 flex flex-col" : "flex flex-col h-screen"}
        style={{ background: "#f0f4f8" }}
      >

        {/* ── STICKY HEADER ─────────────────────────────────────────── */}
        <div
          className="sticky top-0 z-20 border-b border-slate-200 px-6 py-3 bg-white"
          
          style={{ boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2.5">
              {onClose && (
                <button
                  onClick={onClose}
                  className="ml-2 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                  title="Close"
                >
                  <XMarkIcon className="w-8 h-8" />
                </button>
              )}
              <div className="w-1 h-6 rounded-full"
                style={{ background: "linear-gradient(180deg,#1e40af,#0369a1)" }} />
              <h2 className="text-lg font-bold tracking-tight text-slate-800">Ledgers</h2>
              {/* {onClose && (
                <button
                  onClick={onClose}
                  className="ml-2 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                  title="Close"
                >
                  <XMarkIcon className="w-10 h-10" />
                </button>
              )} */}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Search */}
              <div className="flex w-full sm:w-[300px]">
                <input
                  type="text"
                  placeholder="Search by name, company or GST..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-l-lg bg-slate-50 border border-slate-200 px-4 py-2 text-sm
                             text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  className="px-3 rounded-r-lg text-white"
                  style={{ background: "linear-gradient(135deg,#1e40af,#0369a1)" }}
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Show entries */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Show</label>
                <select
                  value={limit}
                  onChange={(e) => onLimitChange(Number(e.target.value))}
                  className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm
                             text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-xs text-slate-500">entries</span>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => { setNewLedger({ ...blankLedger }); setErrors({}); setAddLedgerModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90"
                  style={{ background: "linear-gradient(135deg,#1e40af,#0369a1)", boxShadow: "0 2px 8px rgba(30,64,175,0.18)" }}
                >
                  <PlusIcon className="h-4 w-4" /> Add Ledger
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── TABLE ─────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden p-5">
          <div
            className="rounded-xl overflow-hidden h-full flex flex-col bg-white border border-slate-200"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
          >
            {/* header stays fixed, only body scrolls */}
            <div className="flex-1 overflow-y-auto overflow-x-auto att-scrollbar">
              <table className="min-w-[1550px] w-full text-sm text-left border-collapse">

                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                  <tr>
                    {[
                      { filter: <TableHeaderFilter label="Ledger Name" options={ledgerNameOptions} selectedValues={ledgerNameFilter} onChange={setLedgerNameFilter} /> },
                      { filter: <TableHeaderFilter label="Company Name" options={companyOptions} selectedValues={companyFilter} onChange={setCompanyFilter} /> },
                      { filter: <TableHeaderFilter label="Branch" options={branchOptionsFilter} selectedValues={branchFilter} onChange={setBranchFilter} /> },
                      { filter: <TableHeaderFilter label="Product" options={productOptionsFilter} selectedValues={productFilter} onChange={setProductFilter} /> },
                      { filter: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone</span> },
                      { filter: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">GST No.</span> },
                      { filter: <TableHeaderFilter label="State" options={stateOptionsFilter} selectedValues={stateFilter} onChange={setStateFilter} /> },
                      { filter: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pincode</span> },
                      { filter: <TableHeaderFilter label="Status" options={statusOptionsFilter} selectedValues={statusFilter} onChange={setStatusFilter} /> },
                      { filter: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Updated By</span> },
                      { filter: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Updated At</span> },
                      { filter: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</span> },
                    ].map((col, i) => (
                      <th key={i} className="py-3 px-4 text-left border-b border-slate-200 bg-inherit">
                        {col.filter}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filteredLedgers.length ? (
                    filteredLedgers
                      .slice((currentPage - 1) * limit, currentPage * limit)
                      .map((ledger, idx) => (
                        <tr
                          key={ledger.slNo ?? idx}
                          className="border-t border-slate-100 hover:bg-blue-50/40 transition-colors"
                        >
                          <td className="py-3 px-4 text-slate-800 text-xs font-medium truncate max-w-[180px]">
                            {ledger.ledger_name || '—'}
                          </td>
                          <td className="py-3 px-4 text-slate-700 text-xs truncate max-w-[180px]">
                            {ledger.companyName || '—'}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                            {ledger.branch || '—'}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs truncate max-w-[140px]">
                            {ledger.product || '—'}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                            {ledger.phone_number || '—'}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                            {ledger.gst_no || '—'}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                            {ledger.state || '—'}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                            {ledger.pincode || '—'}
                          </td>
                          <td className="py-3 px-4 text-xs">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                              Number(ledger.is_active) === 1
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-rose-100 text-rose-700"
                            }`}>
                              {Number(ledger.is_active) === 1 ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                            {ledger.updatedBy || '—'}
                          </td>
                          <td className="py-3 px-4 text-slate-400 text-xs whitespace-nowrap">
                            {formatIST(ledger.updated_at)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => openEditLedger(ledger, e)}
                                title="Update ledger"
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold
                                           text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100"
                              >
                                <PencilIcon className="w-3.5 h-3.5" /> Update
                              </button>
                              <button
                                onClick={(e) => openMarkAsLead(ledger, e)}
                                title="Mark as lead"
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold
                                           text-white hover:opacity-90 whitespace-nowrap"
                                style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}
                              >
                                <UserPlusIcon className="w-3.5 h-3.5" /> Mark as Lead
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan={12} className="py-12 text-center text-slate-400 text-sm">
                        No ledgers found with current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── PAGINATION ────────────────────────────────────────────── */}
        <div
          className="sticky bottom-0 z-10 border-t border-slate-200 px-6 py-3 bg-white"
          style={{ boxShadow: "0 -1px 4px rgba(15,23,42,0.06)" }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="text-xs text-slate-500 font-medium">
              Showing <span className="text-slate-700 font-bold">{startEntry}</span> to{' '}
              <span className="text-slate-700 font-bold">{endEntry}</span> of{' '}
              <span className="text-slate-700 font-bold">{filteredLedgers.length}</span> entries
            </div>
            <div className="inline-flex items-center gap-2">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100
                           text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <div className="px-4 py-2 rounded-lg border border-slate-200 bg-slate-50
                              text-xs text-slate-600 font-semibold">
                Page {currentPage} of {totalPagesCalc || 1}
              </div>
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPagesCalc}
                className="p-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100
                           text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          ADD NEW LEDGER MODAL
      ════════════════════════════════════════════════════════ */}
      {addLedgerModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}
        >
          <div
            className="w-full max-w-3xl rounded-2xl bg-white border border-slate-200 p-6
                       overflow-y-auto max-h-[90vh] att-scrollbar"
            style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
          >
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full"
                  style={{ background: "linear-gradient(180deg,#1e40af,#0369a1)" }} />
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <PlusIcon className="h-5 w-5 text-blue-600" /> Add New Ledger
                </h3>
              </div>
              <button onClick={() => setAddLedgerModal(false)} className="text-slate-400 hover:text-slate-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ledgerFormFields.map((field) => (
                <div key={field.name} className={field.colSpan === 2 ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    {field.label}
                    {field.required && <span className="text-rose-500 ml-1">*</span>}
                  </label>
                  {renderFormField(field, newLedger[field.name], handleNewLedgerChange, false)}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setAddLedgerModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600
                           border border-slate-200 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={saveLedger}
                disabled={saving}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90 disabled:opacity-60"
                style={{ background: "linear-gradient(135deg,#1e40af,#0369a1)", boxShadow: "0 2px 8px rgba(30,64,175,0.18)" }}
              >
                {saving ? "Saving..." : "Save Ledger"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          UPDATE LEDGER MODAL
      ════════════════════════════════════════════════════════ */}
      {editLedgerModal && selectedLedger && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}
        >
          <div
            className="w-full max-w-3xl rounded-2xl bg-white border border-slate-200 p-6
                       overflow-y-auto max-h-[90vh] att-scrollbar"
            style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
          >
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full"
                  style={{ background: "linear-gradient(180deg,#1e40af,#0369a1)" }} />
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <PencilIcon className="h-5 w-5 text-blue-600" /> Update Ledger
                </h3>
              </div>
              <button onClick={() => setEditLedgerModal(false)} className="text-slate-400 hover:text-slate-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ledgerFormFields.map((field) => (
                <div key={field.name} className={field.colSpan === 2 ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    {field.label}
                    {field.required && <span className="text-rose-500 ml-1">*</span>}
                  </label>
                  {renderFormField(
                    field,
                    selectedLedger[field.name],
                    handleEditLedgerChange,
                    nonEditableLedgerFields.includes(field.name)
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditLedgerModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600
                           border border-slate-200 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={updateLedger}
                disabled={saving}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90 disabled:opacity-60"
                style={{ background: "linear-gradient(135deg,#059669,#10b981)", boxShadow: "0 2px 8px rgba(5,150,105,0.18)" }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          MARK AS LEAD MODAL
      ════════════════════════════════════════════════════════ */}
      {markAsLeadModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}
        >
          <div
            className="w-full max-w-3xl rounded-2xl bg-white border border-slate-200 p-6
                       overflow-y-auto max-h-[90vh] att-scrollbar"
            style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
          >
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full"
                  style={{ background: "linear-gradient(180deg,#059669,#10b981)" }} />
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <UserPlusIcon className="h-5 w-5 text-emerald-600" /> Create Lead From Ledger
                </h3>
              </div>
              <button onClick={() => setMarkAsLeadModal(false)} className="text-slate-400 hover:text-slate-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {leadFormFields.map((field) => (
                <div key={field.name} className={field.colSpan === 2 ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    {field.label}
                    {field.required && <span className="text-rose-500 ml-1">*</span>}
                  </label>
                  {renderFormField(
                    { ...field, errsSource: leadErrors },
                    newLead[field.name],
                    handleNewLeadChange,
                    field.nonEditable
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setMarkAsLeadModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600
                           border border-slate-200 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={saveLeadFromLedger}
                disabled={saving}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90 disabled:opacity-60"
                style={{ background: "linear-gradient(135deg,#059669,#10b981)", boxShadow: "0 2px 8px rgba(5,150,105,0.18)" }}
              >
                {saving ? "Creating..." : "Create Lead"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LedgerTable;